import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ServiceFAQ } from '@/components/sections/ServiceFAQ';
import { useJsonLd } from '@/hooks/useJsonLd';

const accountingFAQs = [
  { q: "How much does a bookkeeper or accountant cost in Charlotte, NC?", a: "Monthly bookkeeping typically ranges based on transaction volume and complexity. KGOB provides CPA-led bookkeeping — not outsourced data entry — which means your financials are reviewed by experienced accountants every month. We serve businesses at our Charlotte, Winston-Salem, and Mooresville offices, and remotely across the United States." },
  { q: "What's the difference between bookkeeping and accounting?", a: "Bookkeeping is recording daily transactions — categorizing expenses, reconciling bank statements, maintaining the general ledger. Accounting goes further: analyzing financial data, preparing statements, providing strategic insights, and ensuring tax compliance. KGOB provides both as an integrated service so your books feed directly into tax planning and business decisions." },
  { q: "Can KGOB set up or fix my QuickBooks?", a: "Yes. We handle QuickBooks setup from scratch, chart of accounts configuration, bank feed connections, and cleanup of existing files that have gotten messy. Many clients come to us with years of uncategorized transactions — we bring them current and set up systems that stay clean going forward." },
  { q: "Do I need a CPA for payroll, or can I use software?", a: "Payroll software handles the mechanics, but a CPA ensures compliance — correct tax withholding, timely quarterly filings (941/940), W-2 and 1099 preparation, and state-specific requirements. Payroll mistakes lead to IRS penalties faster than almost anything else. KGOB coordinates payroll processing with your accounting so everything stays aligned." },
  { q: "What does payroll management include at KGOB?", a: "Payroll management at KGOB covers the full cycle: running payroll on schedule, tax withholding and deposits, quarterly and annual filings, W-2s and 1099s, and keeping payroll aligned with your books. You get one CPA-led team managing payroll and accounting together instead of separate vendors pointing at each other." },
  { q: "How often should a small business owner review their financials?", a: "Monthly. At minimum. You should know your revenue, expenses, profit margin, and cash flow every month — not just at tax time. KGOB delivers clean monthly financial statements with a plain-language summary so you always know where your business stands." }
];

const AccountingPayroll = () => {
    useJsonLd('accounting-0', {"@context": "https://schema.org", "@type": "Service", "name": "Accounting and Payroll Services", "description": "CPA-led accounting and payroll services with payroll management, monthly bookkeeping, QuickBooks setup, financial statements, and catch-up accounting for growing businesses. Charlotte, NC firm serving clients nationwide.", "url": "https://kgob.com/services/accounting-payroll", "provider": {"@type": "AccountingService", "name": "KGOB CPAs & Advisors", "url": "https://kgob.com", "address": {"@type": "PostalAddress", "addressLocality": "Charlotte", "addressRegion": "NC", "addressCountry": "US"}}, "serviceType": ["Bookkeeping", "Payroll Management", "Payroll Processing", "QuickBooks Setup", "Financial Statements", "Accounts Payable", "Accounts Receivable"], "areaServed": {"@type": "Country", "name": "United States"}});

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Accounting and Payroll Services | Payroll Management for Growing Businesses | KGOB</title>
        <meta name="description" content="CPA-led accounting and payroll services with clear payroll management, bookkeeping, and reporting designed for growing businesses." />
        <link rel="canonical" href="https://kgob.com/services/accounting-payroll" />
        <meta property="og:title" content="Accounting and Payroll Services | Payroll Management for Growing Businesses | KGOB" />
        <meta property="og:description" content="CPA-led monthly bookkeeping, payroll processing, QuickBooks setup, and financial statements for growing businesses." />
        <meta property="og:url" content="https://kgob.com/services/accounting-payroll" />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#0B2240] via-[#0f2d4f] to-[#0B2240] py-20 lg:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h1 className="font-['Playfair_Display'] text-5xl font-bold text-[#F7F6F2] mb-6">
                Accounting and Payroll Services
              </h1>
              
              <p className="text-xl text-[#D4AF37] italic mb-4">
                Clarity without chaos. CPA-led monthly books and payroll management for growing businesses.
              </p>
              
              <p className="text-lg text-[#F7F6F2]/90 mb-6">
                KGOB provides accounting and payroll services with hands-on payroll management and monthly bookkeeping designed for growing businesses. Clean books make tax, valuation, and decisions clearer. Most long-term KGOB relationships begin here.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Button data-testid="accounting-payroll-button-4" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                  <Link data-testid="accounting-payroll-link-4" to="/book-consultation?source=services-accounting">Request Accounting Review</Link>
                </Button>
                <Button data-testid="accounting-payroll-button-3" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                  <Link data-testid="accounting-payroll-link-3" to="/services">View All Services</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Keyword focus */}
        <section className="py-16 bg-white" data-testid="payroll-keyword-section">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center">
            <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-[#0B2240] mb-4">
              Accounting, bookkeeping, and payroll management
            </h2>
            <p className="text-lg text-[#4C5A70] max-w-3xl mx-auto">
              Monthly bookkeeping, payroll management, and clear reporting from one CPA-led team, so your numbers are always ready when a decision needs them.
            </p>
          </div>
        </section>

        {/* Is This Right for You? */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-8 text-center">
              Is This Right for You?
            </h2>
            
            <Card className="p-8 border-2 border-[#E1DFD7]">
              <p className="text-[#4C5A70] mb-6">
                This service is designed for business owners who need reliable, month-over-month accounting and payroll support. You will benefit most if:
              </p>
              
              <div className="space-y-3">
                {[
                  'Your books are behind, unclear, or managed inconsistently',
                  'You need monthly financial statements you can actually understand and use',
                  'You want payroll handled correctly, on time, and integrated with your books',
                  'You are preparing for tax planning, a loan application, or investor review',
                  'You want a CPA-led team managing your financial foundation'
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-[#4C5A70]">{item}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        {/* What You Get */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              What You Get
            </h2>
            
            <div className="space-y-8">
              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Monthly Bookkeeping & Reconciliation</h3>
                <p className="text-[#4C5A70] mb-4">
                  We record, categorize, and reconcile all your transactions each month. This includes bank accounts, credit cards, loans, and other accounts.
                </p>
                <ul className="space-y-2">
                  {[
                    'Transaction coding and categorization',
                    'Bank and credit card reconciliations',
                    'Monthly financial statements (P&L, Balance Sheet, Cash Flow)',
                    'Access to your QuickBooks Online file anytime'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">QuickBooks Setup & Cleanup</h3>
                <p className="text-[#4C5A70] mb-4">
                  If you are starting fresh or need a cleanup, we will set up or repair your QuickBooks Online file with a chart of accounts designed for your business.
                </p>
                <ul className="space-y-2">
                  {[
                    'Custom chart of accounts aligned with your business model',
                    'Historical cleanup (catching up from prior months)',
                    'Integration with your bank feeds and payment processors',
                    'Training for you and your team on how to use the system'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Payroll Administration</h3>
                <p className="text-[#4C5A70] mb-4">
                  We handle your payroll processing, tax deposits, quarterly filings, and year-end W-2 preparation. Your payroll data flows directly into your books.
                </p>
                <ul className="space-y-2">
                  {[
                    'Bi-weekly, semi-monthly, or monthly payroll runs',
                    'Federal and state payroll tax deposits',
                    'Quarterly Form 941 and state payroll returns',
                    'Year-end W-2 and W-3 preparation and filing'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              How It Works
            </h2>
            
            <div className="space-y-6">
              {[
                { step: '1', title: 'Initial Consultation', desc: 'We review your current setup, discuss your business, and identify any cleanup or setup needs.' },
                { step: '2', title: 'Setup or Cleanup', desc: 'We configure or repair your QuickBooks Online file, set up bank feeds, and establish your chart of accounts.' },
                { step: '3', title: 'Monthly Workflow Begins', desc: 'Each month, we record transactions, reconcile accounts, and prepare your financial statements.' },
                { step: '4', title: 'Payroll Processing', desc: 'If you add payroll, we handle processing, tax deposits, and quarterly filings on schedule.' },
                { step: '5', title: 'Ongoing Support & Review', desc: 'You have access to your KGOB team anytime for questions, guidance, and year-round tax planning.' }
              ].map((item, i) => (
                <Card key={i} className="p-6 border-2 border-[#E1DFD7]">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                      <span className="text-[#0B2240] font-bold text-lg">{item.step}</span>
                    </div>
                    <div>
                      <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-2">{item.title}</h3>
                      <p className="text-[#4C5A70]">{item.desc}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Changes */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              What Changes
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Functional</h3>
                <p className="text-[#4C5A70] text-sm">
                  Your books are current, accurate, and ready for tax planning, loan applications, or investor review.
                </p>
              </Card>

              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Emotional</h3>
                <p className="text-[#4C5A70] text-sm">
                  You feel calm and confident knowing your financial foundation is handled by a CPA-led team.
                </p>
              </Card>

              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Strategic</h3>
                <p className="text-[#4C5A70] text-sm">
                  With clean books, you can make better decisions about hiring, spending, and long-term growth.
                </p>
              </Card>
            </div>
          </div>
        </section>

        <ServiceFAQ faqs={accountingFAQs} />

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-b from-[#0B2240] to-[#0f2d4f]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-4xl font-bold text-[#F7F6F2] mb-6">
              Ready to Get Your Books in Order?
            </h2>
            <p className="text-lg text-[#F7F6F2]/90 mb-10">
              Schedule a consultation to discuss your accounting and payroll needs. We will review your current setup and recommend the right path forward.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button data-testid="accounting-payroll-button-2" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                <Link data-testid="accounting-payroll-link-2" to="/book-consultation?source=services-accounting">Request Accounting Review</Link>
              </Button>
              <Button data-testid="accounting-payroll-button-1" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                <Link data-testid="accounting-payroll-link-1" to="/services">View All Services</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default AccountingPayroll;